import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { formatApiErrorDetail } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import MathCaptcha from '@/components/MathCaptcha';
import {
  Loader2, KeyRound, Eye, EyeOff, Copy, CheckCircle2, ShieldAlert, ArrowLeft, Timer,
} from 'lucide-react';
import { toast } from 'sonner';

const STEPS = { REQUEST: 'request', CODE: 'code', DONE: 'done' };

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.REQUEST);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [issuedCode, setIssuedCode] = useState(null);
  const [captcha, setCaptcha] = useState({ captcha_id: '', answer: '' });
  const captchaRef = useRef(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password', {
        email, captcha_id: captcha.captcha_id, captcha_answer: captcha.answer,
      });
      setIssuedCode(data.code);          // null if email unknown — show generic message
      setStep(STEPS.CODE);
      setCaptcha({ captcha_id: '', answer: '' });
      captchaRef.current?.refresh();
    } catch (err) {
      console.error('[Maarivil] forgot-password request failed:', err);
      setError(formatApiErrorDetail(err.response?.data?.detail, err));
      captchaRef.current?.refresh();
    } finally { setLoading(false); }
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    if (pwd !== pwd2) { setError('Passwords do not match.'); return; }
    if (pwd.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setError(''); setLoading(true);
    try {
      await api.post('/auth/reset-password', {
        email, code, new_password: pwd,
        captcha_id: captcha.captcha_id, captcha_answer: captcha.answer,
      });
      setStep(STEPS.DONE);
      toast.success('Password updated', { description: 'You can sign in with your new password.' });
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      console.error('[Maarivil] reset-password failed:', err);
      setError(formatApiErrorDetail(err.response?.data?.detail, err));
      captchaRef.current?.refresh();
    } finally { setLoading(false); }
  };

  const copyCode = async () => {
    if (!issuedCode) return;
    try {
      await navigator.clipboard.writeText(issuedCode);
      setCopied(true);
      toast.success('Code copied');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy — long-press to copy manually');
    }
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-screen pt-28 pb-24 px-6 sm:px-12 flex items-center justify-center">
      <div className="w-full max-w-md glass-strong rounded-3xl border border-white/10 p-8 sm:p-10">
        <div className="flex justify-center mb-6">
          <img src="/maarivil-logo.png" alt="Maarivil" className="h-16 w-auto object-contain drop-shadow-[0_0_20px_rgba(217,70,239,0.3)]" />
        </div>

        {step === STEPS.REQUEST && (
          <>
            <h1 className="font-display text-3xl font-bold text-center text-white mb-2">Forgot password</h1>
            <p className="text-slate-400 text-center mb-8 text-sm">Enter your account email — we&apos;ll show you a one-time reset code valid for 15 minutes.</p>

            <form onSubmit={requestCode} className="space-y-4">
              <div>
                <Label className="text-xs text-slate-400 uppercase tracking-wider">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  data-testid="forgot-email-input"
                  className="bg-white/5 border-white/10 mt-1"
                />
              </div>
              <MathCaptcha ref={captchaRef} onChange={(v) => setCaptcha({ captcha_id: v.captcha_id, answer: v.answer })} />
              {error && (
                <div data-testid="forgot-error" className="text-rose-300 text-xs flex items-center gap-1.5">
                  <ShieldAlert className="h-3 w-3" /> {error}
                </div>
              )}
              <Button type="submit" disabled={loading} data-testid="forgot-request-button" className="w-full btn-rainbow text-white border-0 rounded-xl py-5">
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Requesting…</> : <><KeyRound className="h-4 w-4 mr-2" /> Get reset code</>}
              </Button>
            </form>
            <div className="text-center mt-6 text-xs text-slate-400">
              <Link to="/login" className="hover:text-fuchsia-300 inline-flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> Back to sign in</Link>
            </div>
          </>
        )}

        {step === STEPS.CODE && (
          <>
            <h1 className="font-display text-3xl font-bold text-center text-white mb-2">Set a new password</h1>
            {issuedCode ? (
              <div className="mb-6 mt-2">
                <p className="text-slate-400 text-xs text-center mb-3">Your one-time reset code (valid for 15 minutes):</p>
                <div data-testid="forgot-issued-code-card" className="glass border border-fuchsia-400/40 rounded-2xl p-5 text-center">
                  <div className="font-mono text-4xl tracking-[0.4em] text-white font-bold select-all" data-testid="forgot-issued-code">{issuedCode}</div>
                  <button
                    type="button"
                    onClick={copyCode}
                    data-testid="forgot-copy-code-button"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs text-fuchsia-300 hover:text-fuchsia-200"
                  >
                    {copied ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? 'Copied' : 'Copy code'}
                  </button>
                  <div className="mt-3 text-[10px] uppercase tracking-widest text-amber-300 flex items-center justify-center gap-1.5">
                    <Timer className="h-3 w-3" /> Expires in 15 min
                  </div>
                </div>
                <p className="text-slate-500 text-[10px] text-center mt-2">Paste it below along with your new password.</p>
              </div>
            ) : (
              <p className="text-slate-400 text-center mb-6 text-sm">If an account exists for <span className="text-white">{email}</span>, a one-time code has been issued. Check your screen / device and paste it below.</p>
            )}

            <form onSubmit={confirmReset} className="space-y-4">
              <div>
                <Label className="text-xs text-slate-400 uppercase tracking-wider">Reset code</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  inputMode="numeric"
                  maxLength={6}
                  data-testid="forgot-code-input"
                  className="bg-white/5 border-white/10 mt-1 font-mono tracking-widest"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-400 uppercase tracking-wider">New password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPwd ? 'text' : 'password'}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    required
                    minLength={6}
                    data-testid="forgot-new-password-input"
                    className="bg-white/5 border-white/10 pr-10"
                  />
                  <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-400 uppercase tracking-wider">Confirm new password</Label>
                <Input
                  type={showPwd ? 'text' : 'password'}
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  required
                  minLength={6}
                  data-testid="forgot-confirm-password-input"
                  className="bg-white/5 border-white/10 mt-1"
                />
              </div>
              <MathCaptcha ref={captchaRef} onChange={(v) => setCaptcha({ captcha_id: v.captcha_id, answer: v.answer })} />
              {error && (
                <div data-testid="forgot-error" className="text-rose-300 text-xs flex items-center gap-1.5">
                  <ShieldAlert className="h-3 w-3" /> {error}
                </div>
              )}
              <Button type="submit" disabled={loading} data-testid="forgot-confirm-button" className="w-full btn-rainbow text-white border-0 rounded-xl py-5">
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Resetting…</> : 'Reset password'}
              </Button>
            </form>
            <div className="text-center mt-6 text-xs text-slate-400">
              <button type="button" onClick={() => { setStep(STEPS.REQUEST); setError(''); setIssuedCode(null); setCode(''); setPwd(''); setPwd2(''); captchaRef.current?.refresh(); }} data-testid="forgot-back-button" className="hover:text-fuchsia-300 inline-flex items-center gap-1">
                <ArrowLeft className="h-3 w-3" /> Use a different email
              </button>
            </div>
          </>
        )}

        {step === STEPS.DONE && (
          <div data-testid="forgot-success" className="text-center py-6">
            <div className="h-16 w-16 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-300" />
            </div>
            <h2 className="font-display text-2xl text-white mb-2">Password updated</h2>
            <p className="text-slate-400 text-sm">Redirecting you to sign in…</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
